import React from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { JsonTree } from "@/components/ui/json-tree";
import { Charge } from "@/model/interface/Charge";
import { CHARGE_TYPE_LABELS, ChargeType } from "@/enum/ChargeType";
import { chargeUserName, formatChargeAmount } from "@/util/charge";
import { formatDateTime, formatTime } from "@/util/date";

const QUANTITY_LABELS: Partial<Record<ChargeType, string>> = {
  [ChargeType.SKIP_TRACE]: "Hits",
  [ChargeType.DNC_SCRUB]: "Numbers checked",
  [ChargeType.SMS]: "Messages",
  [ChargeType.PROPERTY_DATA_OVERAGE]: "Leads",
  [ChargeType.RECORDING_STORAGE]: "Minutes stored",
};

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b py-2.5 text-sm last:border-b-0">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right font-medium">{children}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">{children}</p>;
}

interface ChargeDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  charge?: Charge;
  isLoading: boolean;
  showInternal?: boolean;
}

export default function ChargeDetailsSheet({
  open,
  onOpenChange,
  charge,
  isLoading,
  showInternal = false,
}: ChargeDetailsSheetProps) {
  const context = charge?.context;
  const contacts = context?.contacts ?? [];
  const numbersWithoutContact = (context?.phoneNumbers ?? []).filter(
    (phoneNumber) => !contacts.some((contact) => contact.phoneNumber === phoneNumber),
  );
  const direction = context?.direction ? context.direction.charAt(0) + context.direction.slice(1).toLowerCase() : null;
  const amount = charge?.amount ?? 0;
  const platformFee = charge?.platformFee ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto text-foreground sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{charge ? CHARGE_TYPE_LABELS[charge.type] ?? charge.type : "Charge"}</SheetTitle>
          <SheetDescription>{charge ? formatDateTime(charge.createdAt) : ""}</SheetDescription>
        </SheetHeader>

        {isLoading || !charge ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            <p className="text-3xl font-semibold tabular-nums">{formatChargeAmount(amount)}</p>

            <div>
              <SectionLabel>Details</SectionLabel>
              <DetailRow label="By">{chargeUserName(charge.user)}</DetailRow>
              {charge.description && <DetailRow label="Description">{charge.description}</DetailRow>}
              {context?.contactId && (
                <DetailRow label="Contact">
                  <Link to={`/contacts/${context.contactId}/detail`} className="text-primary hover:underline">
                    {context.contactName ?? "View contact"}
                  </Link>
                </DetailRow>
              )}
              {context?.contactListId && (
                <DetailRow label="List">
                  <Link to={`/contacts/lists/${context.contactListId}/edit`} className="text-primary hover:underline">
                    {context.contactListName ?? "View list"}
                  </Link>
                </DetailRow>
              )}
              {context?.batchLabel && <DetailRow label="Batch">{context.batchLabel}</DetailRow>}
              {context?.aiAgentName && <DetailRow label="AI agent">{context.aiAgentName}</DetailRow>}
              {direction && <DetailRow label="Direction">{direction}</DetailRow>}
              {context?.fromNumber && <DetailRow label="From">{context.fromNumber}</DetailRow>}
              {context?.toNumber && <DetailRow label="To">{context.toNumber}</DetailRow>}
              {context?.durationSeconds != null && (
                <DetailRow label="Duration">
                  <span className="tabular-nums">{formatTime(context.durationSeconds)}</span>
                </DetailRow>
              )}
              {context?.quantity != null && (
                <DetailRow label={QUANTITY_LABELS[charge.type] ?? "Quantity"}>
                  <span className="tabular-nums">{context.quantity}</span>
                </DetailRow>
              )}
              {context?.bulk != null && <DetailRow label="Mode">{context.bulk ? "Bulk" : "Instant"}</DetailRow>}
              {context?.phoneNumberType && <DetailRow label="Number type">{context.phoneNumberType}</DetailRow>}
              {context?.periodStart && context?.periodEnd && (
                <DetailRow label="Period">
                  {format(parseISO(context.periodStart), "MMM d, yyyy")} to{" "}
                  {format(parseISO(context.periodEnd), "MMM d, yyyy")}
                </DetailRow>
              )}
              {context?.callId && (
                <DetailRow label="Call">
                  <Link to={`/calls/${context.callId}`} className="text-primary hover:underline">
                    View call
                  </Link>
                </DetailRow>
              )}
            </div>

            {(contacts.length > 0 || numbersWithoutContact.length > 0) && (
              <div>
                <SectionLabel>Numbers</SectionLabel>
                <div className="max-h-72 overflow-y-auto rounded-lg border">
                  {contacts.map((contact) => (
                    <div
                      key={`${contact.contactId}-${contact.phoneNumber}`}
                      className="flex items-center justify-between gap-4 border-b px-3 py-2 text-sm last:border-b-0"
                    >
                      <Link to={`/contacts/${contact.contactId}/detail`} className="truncate text-primary hover:underline">
                        {contact.contactName ?? "Unnamed contact"}
                      </Link>
                      <span className="shrink-0 tabular-nums text-muted-foreground">{contact.phoneNumber}</span>
                    </div>
                  ))}
                  {numbersWithoutContact.map((phoneNumber) => (
                    <div key={phoneNumber} className="border-b px-3 py-2 text-sm tabular-nums last:border-b-0">
                      {phoneNumber}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showInternal && (
              <div>
                <SectionLabel>Internal</SectionLabel>
                <DetailRow label="Carrier cost">
                  <span className="tabular-nums">${(amount - platformFee).toFixed(4)}</span>
                </DetailRow>
                <DetailRow label="Platform fee">
                  <span className="tabular-nums">${platformFee.toFixed(4)}</span>
                </DetailRow>
                {context?.provider && <DetailRow label="Provider">{context.provider}</DetailRow>}
                {context?.credits != null && (
                  <DetailRow label="Credits">
                    <span className="tabular-nums">{context.credits}</span>
                  </DetailRow>
                )}
                <div className="mt-3">
                  {context ? (
                    <JsonTree value={context} />
                  ) : (
                    <p className="text-sm text-muted-foreground">No context recorded for this charge.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
